import { CustomLoader } from "@admin/components/ui/custom-loader";
import { Card, CardContent } from "../../components/ui/card";
import { TableCell, TableHead } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Eye } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  smsLogsAPI,
  type SmsLog,
  type PaginationParams,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";
import { SmsLogDetailsModal } from "../../components/communication-logs/SmsLogDetailsModal";
import {
  StatusBadge,
  TypeBadge,
} from "../../components/communication-logs/LogBadges";
import {
  formatLogTimestamp,
  getSmsAction,
  getSmsConfig,
  getSmsRecipient,
  getSmsStatus,
  getSmsTenantName,
  getSmsTimestamp,
  getSmsType,
  getSmsVerifiedAt,
} from "../../lib/communicationLogUtils";
import { DataTable, type TableColumnDef } from "../../components/data-table";

const tableHeadClass =
  "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

export default function SmsLogs() {
  const [logs, setLogs] = useState<SmsLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set()
  );
  const [selectedLog, setSelectedLog] = useState<SmsLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginationParams = {
        page: currentPage,
        page_size: pageSize,
      };
      const res = await smsLogsAPI.list(params);
      const list = res.results ?? res.data ?? [];
      const count = res.count ?? res.total_count ?? list.length ?? 0;
      setLogs(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
      setSelectedIds(new Set());
    } catch {
      toast.error("Failed to load SMS logs");
      setLogs([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const allSelected =
    logs.length > 0 && logs.every(log => selectedIds.has(log.id));

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(logs.map(log => log.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleRow = (id: string | number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleViewLog = useCallback(async (log: SmsLog) => {
    setDetailOpen(true);
    setSelectedLog(null);
    setLoadingDetail(true);
    try {
      const detail = await smsLogsAPI.getById(log.id);
      setSelectedLog(detail ?? log);
    } catch {
      toast.error("Failed to load SMS log details");
      setSelectedLog(log);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const smsTableColumns = useMemo<TableColumnDef<SmsLog>[]>(
    () => [
      {
        id: "timestamp",
        label: "Timestamp",
        width: 160,
        headerClassName: tableHeadClass,
        cellClassName: "whitespace-nowrap text-sm text-foreground",
        renderCell: log => formatLogTimestamp(getSmsTimestamp(log)),
      },
      {
        id: "recipient",
        label: "Recipient",
        width: 200,
        headerClassName: tableHeadClass,
        renderCell: log => (
          <>
            <p className="font-medium text-foreground">
              {getSmsRecipient(log)}
            </p>
            <p className="text-sm text-muted-foreground">
              {getSmsTenantName(log)}
            </p>
          </>
        ),
      },
      {
        id: "type",
        label: "OTP Type",
        width: 120,
        headerClassName: tableHeadClass,
        renderCell: log => <TypeBadge value={getSmsType(log)} />,
      },
      {
        id: "action",
        label: "Action",
        width: 100,
        headerClassName: tableHeadClass,
        renderCell: log => <TypeBadge value={getSmsAction(log)} />,
      },
      {
        id: "resource",
        label: "Resource",
        width: 220,
        headerClassName: tableHeadClass,
        cellClassName: "max-w-[220px] truncate text-sm text-foreground",
        renderCell: log => (
          <span className="block truncate" title={getSmsConfig(log)}>
            {getSmsConfig(log)}
          </span>
        ),
      },
      {
        id: "verified",
        label: "Verified At",
        width: 160,
        headerClassName: tableHeadClass,
        cellClassName: "whitespace-nowrap text-sm text-muted-foreground",
        renderCell: log =>
          getSmsVerifiedAt(log)
            ? formatLogTimestamp(getSmsVerifiedAt(log))
            : "—",
      },
      {
        id: "status",
        label: "Status",
        width: 120,
        headerClassName: tableHeadClass,
        renderCell: log => <StatusBadge status={getSmsStatus(log)} />,
      },
      {
        id: "details",
        label: "Details",
        width: 80,
        alwaysVisible: true,
        headerClassName: `${tableHeadClass} text-center`,
        cellClassName: "text-center",
        renderCell: log => (
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={() => handleViewLog(log)}
            title="View details"
          >
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [handleViewLog]
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">SMS Logs</h1>
        <p className="text-muted-foreground">
          View Login OTP and signup SMS audit logs from the platform
        </p>
      </div>

      <Card>
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <CustomLoader size="md" />
            </div>
          ) : (
            <>
              <DataTable
                title={`All SMS Logs (${totalItems})`}
                tableId="super-admin-sms-logs"
                columns={smsTableColumns}
                data={logs}
                getRowKey={log => log.id}
                emptyMessage="No SMS logs found"
                headerRowClassName="bg-muted/40 hover:bg-muted/40"
                leadingColumnCount={1}
                renderLeadingHead={() => (
                  <TableHead className="w-12 pl-6">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={v => toggleAll(v === true)}
                      aria-label="Select all"
                    />
                  </TableHead>
                )}
                renderLeadingCell={log => (
                  <TableCell className="pl-6">
                    <Checkbox
                      checked={selectedIds.has(log.id)}
                      onCheckedChange={v => toggleRow(log.id, v === true)}
                      aria-label="Select row"
                    />
                  </TableCell>
                )}
                rowClassName={() => "hover:bg-muted/30"}
              />

              {totalItems > 0 && (
                <div className="border-t px-2 py-4">
                  <CommonPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalCount={totalItems}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={size => {
                      setPageSize(size);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <SmsLogDetailsModal
        open={detailOpen}
        log={selectedLog}
        loading={loadingDetail}
        onOpenChange={open => {
          setDetailOpen(open);
          if (!open) {
            setSelectedLog(null);
          }
        }}
      />
    </div>
  );
}
